import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useVirtualizer } from '@tanstack/react-virtual';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { 
  ClipboardCheck, 
  Activity, 
  Clock, 
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Zap,
  Globe,
  Layers,
  Search,
  Check,
  Copy,
  Rocket,
  ShieldCheck,
  Cpu,
  History,
  Layout,
  Monitor,
  Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';
import EmptyState from '../components/EmptyState';
import FilterModal from '../components/FilterModal';
import Skeleton from '../components/Skeleton';

const StatCard = ({ label, value, description, icon: Icon, colorClass, cardBg, loading }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`glass-card p-6 relative overflow-hidden group hover:bg-white/[0.04] transition-all duration-500 rounded-[2.5rem] border ${cardBg ? cardBg : 'border-white/5'}`}
  >
    <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full blur-[60px] opacity-10 group-hover:opacity-30 transition-opacity duration-700 ${colorClass}`} />
    
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-2xl bg-white/5 transition-all duration-500 group-hover:scale-110 shadow-xl shadow-black/20 border border-white/10 ${colorClass.replace('bg-', 'text-')}`}>
        {loading ? <Skeleton width="18px" height="18px" /> : <Icon size={18} />}
      </div>
      {loading ? <Skeleton width="60px" height="10px" /> : <div className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] group-hover:text-slate-300 transition-colors">{label}</div>}
    </div>

    <div className="space-y-1 text-left relative z-10">
      {loading ? (
        <Skeleton width="100px" height="40px" borderRadius="0.5rem" />
      ) : (
        <div className="text-4xl font-display font-black text-slate-100 italic tracking-tighter transition-transform group-hover:translate-x-1 duration-500">{value}</div>
      )}
      {loading ? (
        <div className="mt-2 text-left">
          <Skeleton width="120px" height="10px" />
        </div>
      ) : (
        description && <div className="text-[9px] text-slate-500 font-bold uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity">{description}</div>
      )}
    </div>
  </motion.div>
);


const TicketItem = React.memo(({ ticket, isRecentlyProcessed, handleCopyId, copyFeedback, handleMarkAsLive, lang, t }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="glass-card group p-3 px-6 hover:z-50 border-white/5 hover:bg-white/[0.03] transition-all duration-300 flex items-center gap-6 overflow-visible rounded-2xl relative"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center border shrink-0 transition-transform group-hover:scale-110 ${
        ticket.status === 'in_progress'
          ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.1)]'
          : ticket.status === 'applied' 
            ? 'bg-amber-500/10 border-amber-500/20 text-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.1)]' 
            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.1)]'
      }`}>
        {ticket.status === 'in_progress' ? <Cpu size={18} className="animate-pulse" /> : ticket.status === 'applied' ? <Monitor size={18} /> : <ShieldCheck size={18} />}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <div className="relative">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleCopyId(ticket);
              }}
              className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border transition-all cursor-pointer active:scale-95 flex items-center gap-1.5 group/id ${
                copyFeedback === ticket._id 
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' 
                  : 'text-primary bg-primary/5 border-primary/10 hover:bg-primary/20'
              }`}
              title={t('clickToCopy') || 'Click to copy ID'}
            >
              <AnimatePresence mode="wait">
                {copyFeedback === ticket._id ? (
                  <motion.div
                    key="copied"
                    initial={{ opacity: 0, x: 5 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -5 }}
                    className="flex items-center gap-1.5"
                  >
                    <Check size={9} />
                    {t('copied') || '¡COPIADO!'}
                  </motion.div>
                ) : (
                  <motion.div
                    key="id"
                    initial={{ opacity: 0, x: 5 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -5 }}
                    className="flex items-center gap-1.5"
                  >
                    <Copy size={9} className="opacity-0 group-hover/id:opacity-100 transition-opacity" />
                    {ticket.key || `VG-${ticket._id.slice(-4).toUpperCase()}`}
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
          <h3 className="text-sm font-bold text-slate-200 truncate group-hover:text-white transition-colors">
            {ticket.message || ticket.title}
          </h3>
        </div>
        <div className="flex items-center gap-4 text-[9px] text-slate-500 font-bold uppercase tracking-widest opacity-60">
           <span>{new Date(ticket.resolvedAt || ticket.updatedAt).toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</span>
           <span className="w-1 h-1 rounded-full bg-slate-700" />
           <span>{new Date(ticket.resolvedAt || ticket.updatedAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short' })}</span>
        </div>
      </div>

      <div className="hidden lg:block flex-[0.7] px-4">
        <HorizontalTimeline 
          history={ticket.statusHistory}
          currentStatus={ticket.status} 
          t={t} 
          ticketId={ticket._id}
        />
      </div>

      <div className="w-40 flex justify-end">
        {ticket.status === 'applied' ? (
          <button
            onClick={() => handleMarkAsLive(ticket._id)}
            disabled={isRecentlyProcessed}
            className={`group/btn relative px-4 py-2 rounded-xl border transition-all duration-500 flex items-center gap-2 font-black uppercase tracking-widest text-[9px] ${
              isRecentlyProcessed
                ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400 cursor-not-allowed'
                : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 hover:bg-emerald-500/20 hover:border-emerald-500/40 hover:text-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.2)] shadow-lg'
            }`}
          >
            {isRecentlyProcessed ? (
              <>
                <RefreshCw size={12} className="animate-spin" />
                <span>{t('deploying') || 'DEPLOYANDO...'}</span>
              </>
            ) : (
              <>
                <Rocket size={12} className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                <span>{t('markAsLive') || 'MARCAR DEPLOYADO'}</span>
              </>
            )}
          </button>
        ) : ticket.status === 'ready_for_review' || ticket.status === 'completed' || ticket.status === 'done' ? (
          <div className="flex items-center gap-2 text-emerald-500/60 font-black text-[9px] uppercase tracking-widest pr-4">
            <ShieldCheck size={14} />
            <span>{t('deployed') || 'DEPLOYADO'}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-indigo-500/60 font-black text-[9px] uppercase tracking-widest pr-4">
            <Cpu size={14} className="animate-pulse" />
            <span>{t('aiProcessing') || 'IA PROCESANDO'}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
});

const HorizontalTimeline = React.memo(({ history, currentStatus, t, ticketId }) => {
  const steps = [
    { key: 'pending', label: t('timelinePending'), icon: Clock },
    { key: 'in_progress', label: t('timelineLocked'), icon: Cpu, activeColor: 'bg-indigo-500' },
    { key: 'applied', label: t('timelineApplied'), icon: Monitor, activeColor: 'bg-amber-500' },
    { key: 'live', label: t('timelineReady'), icon: Rocket, activeColor: 'bg-emerald-500' }
  ];

  // Map backend ready_for_review/completed/done to our 'live' step
  const getBackendStatus = (stepKey) => {
    if (stepKey === 'live') return ['ready_for_review', 'completed', 'done'];
    return [stepKey];
  };

  const currentIdx = steps.findIndex(s => {
      if (s.key === 'live') return ['ready_for_review', 'completed', 'done'].includes(currentStatus);
      return s.key === currentStatus;
  });

  return (
    <div className="flex items-center gap-2 w-full justify-center">
      {steps.map((step, idx) => {
        const isPast = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const color = isCurrent ? (step.activeColor || 'bg-primary') : isPast ? 'bg-primary/40' : 'bg-white/5';
        const iconColor = isCurrent ? 'text-white' : isPast ? 'text-primary/60' : 'text-slate-600';
        
        // Find matching event in history
        const event = (history || []).find(e => {
            if (step.key === 'live') return ['ready_for_review', 'completed', 'done'].includes(e.status);
            if (step.key === 'pending') return ['pending', 'bug_fixing'].includes(e.status);
            return e.status === step.key;
        });

        return (
          <React.Fragment key={step.key}>
            <div className="relative group/step flex flex-col items-center">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 border border-white/10 relative ${color} ${isCurrent ? 'scale-110' : ''}`}>
                 {/* Glow animation removed as per user request */}
                 <step.icon size={12} className={`relative z-10 ${iconColor}`} />
               </div>
               
               {/* Tooltip on hover */}
               <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover/step:opacity-100 transition-opacity pointer-events-none z-[100]">
                  <div className="bg-slate-900 border border-white/10 px-4 py-2.5 rounded-xl whitespace-nowrap shadow-2xl flex flex-col items-center gap-1">
                    <div className="text-[11px] font-black uppercase tracking-widest text-white">{step.label}</div>
                    {event && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 tabular-nums">
                          {new Date(event.timestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                        </span>
                        <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
                        <span className="text-[10px] font-bold text-primary tabular-nums">
                          {new Date(event.timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
               </div>
            </div>
            
            {idx < steps.length - 1 && (
              <div className="flex-1 h-px bg-white/5 relative overflow-hidden">
                <motion.div 
                    className={`absolute inset-0 ${isPast ? 'bg-primary/30' : 'bg-transparent'}`}
                    animate={{ x: isPast ? 0 : '-100%' }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

const RegressionTicketSkeleton = () => (
   <div className="glass-card group p-3 px-6 border-white/5 bg-white/[0.02] flex items-center gap-6 rounded-2xl relative">
      <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
      
      <div className="flex-1 min-w-0 space-y-2">
         <div className="flex items-center gap-3">
            <Skeleton width="80px" height="18px" borderRadius="0.5rem" />
            <Skeleton width="220px" height="18px" borderRadius="0.5rem" />
         </div>
         <div className="flex items-center gap-4">
            <Skeleton width="60px" height="10px" />
            <Skeleton width="4px" height="4px" borderRadius="50%" />
            <Skeleton width="100px" height="10px" />
         </div>
      </div>

      <div className="hidden lg:block flex-[0.7] px-4">
         <div className="flex items-center gap-2 w-full justify-center">
            {[1, 2, 3, 4].map(idx => (
              <React.Fragment key={idx}>
                <Skeleton width="32px" height="32px" borderRadius="50%" />
                {idx < 4 && <div className="flex-1 h-px bg-white/5" />}
              </React.Fragment>
            ))}
         </div>
      </div>

      <div className="w-40 flex justify-end">
         <Skeleton width="130px" height="36px" borderRadius="0.75rem" />
      </div>
   </div>
);

const RegressionTrackingSkeleton = () => (
  <div className="h-full w-full flex flex-col pt-10 px-10 pb-0 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 relative">
    <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-white/5 shrink-0">
      <div className="flex items-center gap-6">
        <Skeleton width="64px" height="64px" borderRadius="2rem" />
        <div className="space-y-4">
          <Skeleton width="320px" height="40px" />
          <Skeleton width="150px" height="12px" />
        </div>
      </div>
    </header>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map(i => (
         <div key={i} className="glass-card p-6 rounded-[2.5rem] border border-white/5 space-y-4">
            <div className="flex items-center justify-between">
               <Skeleton width="42px" height="42px" borderRadius="1rem" />
               <Skeleton width="80px" height="12px" />
            </div>
            <div className="space-y-2">
               <Skeleton width="100px" height="40px" borderRadius="0.5rem" />
               <Skeleton width="120px" height="10px" />
            </div>
         </div>
      ))}
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
         <Skeleton width="200px" height="24px" />
         <div className="flex items-center gap-3">
           <Skeleton width="300px" height="40px" borderRadius="0.75rem" />
           <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
         </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => <RegressionTicketSkeleton key={i} />)}
      </div>
    </div>
  </div>
);

const RegressionTracking = () => {
  const { activeProjectId, activeProject } = useProject();
  const { t, lang } = useLanguage();
  const { token, personalKey, user } = useAuth();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  
  const [isLive, setIsLive] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: { type: 'all', start: null, end: null }
  });
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  // Robust API Key resolution: support legacy field and new apiKeys array
  const resolvedApiKey = useMemo(() => {
    if (!activeProject) return null;
    return activeProject.apiKey || (activeProject.apiKeys && activeProject.apiKeys.length > 0 ? activeProject.apiKeys[0].key : null);
  }, [activeProject]);

  const SWR_STALE_CONFIG = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 3600000,
  };

  const fetcher = url => api.get(url).then(res => res.data);

  const socketRef = useRef(null);

  // 1. Fetch sprints list (Board strategy)
  const { data: sprintsData = [], isLoading: sprintsLoading } = useSWR(activeProjectId ? `/projects/${activeProjectId}/sprints` : null, fetcher, SWR_STALE_CONFIG);
  
  const sprints = useMemo(() => {
    return Array.isArray(sprintsData?.data) ? sprintsData.data : (Array.isArray(sprintsData) ? sprintsData : []);
  }, [sprintsData]);

  const availableSprints = useMemo(() => sprints.filter(s => s.status !== 'closed' && s.status !== 'completed'), [sprints]);

  // 2. Intelligent selection of the current sprint ID
  const selectedSprintId = useMemo(() => {
    if (availableSprints.length === 0) return null;
    
    // Priority: 'active' status
    const active = availableSprints.find(s => s.status === 'active');
    if (active) return active._id;
    
    // Fallback: Date match
    const now = new Date();
    const dateMatch = availableSprints.find(s => {
      const start = new Date(s.startDate);
      const end = new Date(s.endDate);
      return start <= now && end >= now;
    });
    if (dateMatch) return dateMatch._id;
    
    // Fallback: Latest available
    return availableSprints[0]._id;
  }, [availableSprints]);

  const fetcherWithHeadersInfinite = useCallback(([url, pId, pKey, aKey, sId, page, q, dateRange]) => {
    if (!pId || !pKey || !aKey) return null;
    return api.get(url, {
      params: { 
        sprintId: sId, 
        page, 
        q, 
        limit: 5,
        dateType: dateRange?.type,
        startDate: dateRange?.start,
        endDate: dateRange?.end
      },
      headers: {
        'x-project-id': pId,
        'x-personal-key': pKey,
        'x-api-key': aKey,
        'x-mcp-tool-name': 'get_regression_tracking'
      }
    }).then(res => res.data);
  }, []);

  // 3. Infinite Loading logic
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.pagination?.hasMore) return null;
    if (!activeProjectId || !personalKey || !resolvedApiKey || (!selectedSprintId && availableSprints.length === 0)) return null;
    
    return [
      '/mcp/regression-tracking', 
      activeProjectId, 
      personalKey, 
      resolvedApiKey, 
      selectedSprintId, 
      pageIndex + 1, 
      serverSearchTerm,
      filters.dateRange
    ];
  };

  const { 
    data: infiniteData, 
    size, 
    setSize, 
    isValidating: swrValidating,
    isLoading: swrLoading,
    mutate 
  } = useSWRInfinite(getKey, fetcherWithHeadersInfinite, {
    ...SWR_STALE_CONFIG,
    revalidateFirstPage: false,
    persistSize: true
  });

  const allTickets = useMemo(() => {
    if (!infiniteData) return [];
    const tickets = infiniteData.flatMap(page => page.tickets);
    // Remove duplicates by _id to prevent React key collisions during pagination shifts
    const seen = new Set();
    return tickets.filter(ticket => {
      if (!ticket?._id || seen.has(ticket._id)) return false;
      seen.add(ticket._id);
      return true;
    });
  }, [infiniteData]);

  // Determine if we are performing a local search (frontend-only)
  const isLocalSearch = useMemo(() => searchTerm && !serverSearchTerm, [searchTerm, serverSearchTerm]);

  // Optimized Search: Prioritize local results (Frontend-First)
  useEffect(() => {
    if (!searchTerm) {
      if (serverSearchTerm !== '') setServerSearchTerm('');
      return;
    }

    // Check if we already have it in the currently loaded cache
    const q = searchTerm.toLowerCase();
    const locallyFound = (allTickets || []).some(t => 
       t.message?.toLowerCase().includes(q) ||
       t.title?.toLowerCase().includes(q) ||
       t.key?.toLowerCase().includes(q)
    );

    if (locallyFound) {
       // If found locally, we avoid the server trip and use SWR neutral state
       if (serverSearchTerm !== '') setServerSearchTerm('');
       return;
    }

    // If NOT found locally, debounce and fetch from server
    const timer = setTimeout(() => {
      setServerSearchTerm(searchTerm);
    }, 450); 
    return () => clearTimeout(timer);
  }, [searchTerm, allTickets, serverSearchTerm]);

  const displayDataRaw = useMemo(() => {
    if (!infiniteData?.[0]) return { tickets: [], stats: {}, currentSprint: null, diagnostics: null };
    return {
      ...infiniteData[0],
      tickets: allTickets
    };
  }, [infiniteData, allTickets]);
  
  // Intelligent UI state override: If backend doesn't detect a sprint but we have them locally, use one.
  const displayData = useMemo(() => {
    const data = { ...displayDataRaw };
    if (!data.currentSprint && availableSprints.length > 0) {
      const fallback = availableSprints.find(s => s._id === selectedSprintId) || availableSprints[0];
      data.currentSprint = fallback;
    }
    return data;
  }, [displayDataRaw, availableSprints, selectedSprintId]);

  const [persistentStats, setPersistentStats] = useState(null);

  // Reset persistent stats when the scope changes (project or sprint)
  useEffect(() => {
    setPersistentStats(null);
  }, [activeProjectId, selectedSprintId]);

  // Capture global sprint statistics only when no search filter is applied
  useEffect(() => {
    if (infiniteData?.[0] && !serverSearchTerm && !swrLoading) {
      // Prioritize server-side totals for the entire sprint if provided
      setPersistentStats({
        total: infiniteData[0].pagination?.total || allTickets.length,
        applied: infiniteData[0].stats?.applied ?? allTickets.filter(t => t.status === 'applied').length,
        ready: infiniteData[0].stats?.ready ?? allTickets.filter(t => ['ready_for_review', 'completed', 'done', 'live'].includes(t.status)).length
      });
    }
  }, [infiniteData, serverSearchTerm, swrLoading, allTickets]);

  const mcpStats = useMemo(() => {
    // If we have persistent stats (from a neutral state), use them.
    // Otherwise calculate from currently visible tickets as a fallback.
    if (persistentStats) return persistentStats;
    return {
      total: allTickets.length,
      applied: allTickets.filter(t => t.status === 'applied').length,
      ready: allTickets.filter(t => ['ready_for_review', 'completed', 'done', 'live'].includes(t.status)).length
    };
  }, [persistentStats, allTickets]);

  const loading = swrLoading || (sprintsLoading && !infiniteData);
  const swrError = infiniteData?.[infiniteData.length - 1]?.error;
  const error = swrError?.response?.data?.error || swrError?.message;

  const mutationTimeoutRef = useRef(null);

  // Debounced mutate to avoid performance bottlenecks during message bursts
  const debouncedMutate = useMemo(() => {
    return () => {
      if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);
      mutationTimeoutRef.current = setTimeout(() => {
        mutate();
      }, 100);
    };
  }, [mutate]);

  // Stable ref for mutation to prevent socket reconnection loop
  const mutateRef = useRef(mutate);
  useEffect(() => {
    mutateRef.current = mutate;
  }, [mutate]);

  const trackingScopeRef = useRef({
    userEmail: user?.email,
    selectedSprintId,
    serverSearchTerm,
    dateRange: filters.dateRange
  });
  useEffect(() => {
    trackingScopeRef.current = {
      userEmail: user?.email,
      selectedSprintId,
      serverSearchTerm,
      dateRange: filters.dateRange
    };
  }, [user?.email, selectedSprintId, serverSearchTerm, filters.dateRange]);

  useEffect(() => {
    if (!activeProjectId || !token) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';
    const socket = io(socketUrl, { 
      auth: { token },
      transports: ['websocket'], // Force websocket for stability
      reconnectionAttempts: 5
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-project', activeProjectId);
      setIsLive(true);
    });

    socket.on('disconnect', () => setIsLive(false));

    socket.on('annotation-mutated', (data) => {
      if (data && data.ticket) {
        let wasFound = false;

        const scope = trackingScopeRef.current;
        const isNeutralScope = !scope?.serverSearchTerm && scope?.dateRange?.type === 'all';

        // Surgical cache mutation to avoid full-page reloads and flickering
        mutateRef.current((current) => {
          if (!current || !Array.isArray(current)) return current;
          return current.map(page => {
            if (!page || !Array.isArray(page.tickets)) return page;

            if (data.type === 'update') {
              let pageUpdated = false;
              const newTickets = page.tickets.map(t => {
                if (t._id === data.id || t._id === data.ticket?._id) {
                  wasFound = true;
                  pageUpdated = true;
                  return { ...t, ...data.ticket };
                }
                return t;
              });
              return pageUpdated ? { ...page, tickets: newTickets } : page;
            }

            if (data.type === 'delete') {
              const newTickets = page.tickets.filter(t => t._id !== data.id && t._id !== data.ticket?._id);
              if (newTickets.length !== page.tickets.length) wasFound = true;
              return { ...page, tickets: newTickets };
            }

            return page;
          });
        }, false);

        // INTELLIGENT REAL-TIME UPDATE:
        // If it's a creation, or an update that makes a ticket relevant for this view 
        // (but wasn't in our local list yet), we trigger a full refetch.
        const isRelevant = data.ticket.resolver?.email === scope?.userEmail && 
                          ['in_progress', 'applied', 'ready_for_review', 'completed', 'done', 'live'].includes(data.ticket.status);

        if ((data.type === 'create' || (data.type === 'update' && !wasFound)) && isRelevant) {
          const ticketSprintId = data.ticket?.sprintId;
          const scopeSprintId = scope?.selectedSprintId;
          const isSprintMatch = !scopeSprintId || !ticketSprintId || String(ticketSprintId) === String(scopeSprintId);

          if (isNeutralScope && isSprintMatch) {
            mutateRef.current((current) => {
              if (!current || !Array.isArray(current) || current.length === 0) return current;
              const firstPage = current[0];
              if (!firstPage || !Array.isArray(firstPage.tickets)) return current;

              const id = data.ticket?._id || data.id;
              if (!id) return current;

              const alreadyIn = firstPage.tickets.some(t => t?._id === id);
              if (alreadyIn) return current;

              const limit = firstPage.pagination?.limit || 5;
              const nextTickets = [data.ticket, ...firstPage.tickets].slice(0, limit);
              const nextTotal = (firstPage.pagination?.total || nextTickets.length) + 1;

              const nextFirstPage = {
                ...firstPage,
                tickets: nextTickets,
                pagination: firstPage.pagination ? {
                  ...firstPage.pagination,
                  total: nextTotal,
                  hasMore: true
                } : firstPage.pagination
              };

              const next = [...current];
              next[0] = nextFirstPage;
              return next;
            }, false);
          } else {
            debouncedMutate();
          }
        } else if (data.type === 'create') {
          debouncedMutate();
        }
      } else {
        debouncedMutate();
      }
    });

    socket.on('connect_error', (err) => {
      /* Silent socket error handling for production */
    });

    return () => {
      if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);
      if (socket) {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('annotation-mutated');
        socket.off('connect_error');
        socket.disconnect();
      }
    };
  }, [activeProjectId, token]); // Removed debouncedMutate from deps

  // Handle Infinite Scroll with Intersection Observer
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Only trigger pagination if there is more content AND we are not performing a local filter
        if (entries[0].isIntersecting && !swrLoading && !swrValidating && !isLocalSearch && infiniteData?.[infiniteData.length - 1]?.pagination?.hasMore) {
          setSize(s => s + 1);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [infiniteData, swrLoading, swrValidating, setSize, isLocalSearch]);

  const handleCopyId = useCallback((ticket) => {
    const key = ticket.key || `VG-${ticket._id.slice(-6).toUpperCase()}`;
    navigator.clipboard.writeText(key);
    setCopyFeedback(ticket._id);
    setTimeout(() => setCopyFeedback(null), 2000);
  }, []);

  const handleMarkAsLive = useCallback(async (ticketId) => {
    if (updating[ticketId]) return;
    
    setUpdating(prev => ({ ...prev, [ticketId]: true }));
    
    // Optimistic UI update
    mutateRef.current((current) => {
      if (!current) return current;
      return current.map(page => ({
        ...page,
        tickets: (page.tickets || []).map(t => 
          t._id === ticketId ? { ...t, status: 'ready_for_review' } : t
        )
      }));
    }, false);

    try {
      await api.post('/mcp/annotations/mark-as-live', 
        { ids: [ticketId] },
        { 
          headers: { 
            'x-project-id': activeProjectId,
            'x-personal-key': personalKey,
            'x-api-key': resolvedApiKey,
            'x-mcp-tool-name': 'mark_ticket_as_live'
          } 
        }
      );
    } catch (err) {
      // Revert optimistic update on failure
      debouncedMutate();
    } finally {
      setUpdating(prev => ({ ...prev, [ticketId]: false }));
    }
  }, [activeProjectId, personalKey, resolvedApiKey, updating]);

  const filteredTickets = useMemo(() => {
    const q = searchTerm.toLowerCase();
    const filtered = allTickets.filter(ticket => {
      const matchesSearch = (
        ticket.message?.toLowerCase().includes(q) ||
        ticket.title?.toLowerCase().includes(q) ||
        ticket.key?.toLowerCase().includes(q)
      );

      return matchesSearch;
    });

    return filtered;
  }, [allTickets, searchTerm]);

  // Virtualizer setup
  const rowVirtualizer = useVirtualizer({
    count: filteredTickets.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 90, // Calibrated estimate for high-fidelity rows
    overscan: 10
  });

  if (!activeProjectId) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-10">
        <EmptyState 
          title={t('noProjectSelectedTitle')}
          description={t('noProjectSelectedDesc')}
          icon={Layout}
          action={() => window.location.hash = '/projects'}
          actionLabel={t('goProjects')}
        />
      </div>
    );
  }

  if (loading) return <RegressionTrackingSkeleton />;

  return (
    <div className="h-full w-full flex flex-col pt-10 px-10 pb-0 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 relative">
      {/* Premium Header */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2 border-b border-white/5 shrink-0">
        <div className="space-y-4 text-left">
          <div className="flex items-center gap-2 mb-2">
            {/* Badges removed as per user request */}
          </div>
          
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-[2rem] bg-indigo-500/10 flex items-center justify-center text-indigo-400 shadow-[0_0_50px_rgba(99,102,241,0.15)] border border-indigo-500/20">
              <ShieldCheck size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-display font-black text-white italic tracking-tighter mb-1">
                {t('regressionTracking')}
              </h1>
              <div className="flex items-center gap-2 text-slate-500 text-[9px] font-black uppercase tracking-[0.2em] ml-1 mt-6 opacity-80">
                <Zap size={10} className="text-amber-500" />
                {t('onlyYourTickets')}
              </div>
            </div>
          </div>
        </div>

        {/* Search bar moved below as per user request */}
      </header>

      {!loading && !displayData.currentSprint && resolvedApiKey ? (
        <div className="py-20">
          {(!activeProject?.workspaceId && (!displayData.diagnostics || !displayData.diagnostics.workspaceExists)) ? (
            <EmptyState 
              title={t('noWorkspace')}
              description={t('noWorkspaceDesc')}
              icon={Layers}
              action={() => navigate('/projects')}
              actionLabel={t('goProjects')}
            />
          ) : sprints.length === 0 ? (
            <EmptyState 
              title={t('noSprintsTitle')}
              description={t('noSprintsDesc')}
              icon={Activity}
              action={() => navigate('/board')}
              actionLabel={t('createFirstSprint')}
            />
          ) : availableSprints.length === 0 ? (
            <EmptyState 
              title={t('noActiveSprintTitle')}
              description={t('allSprintsClosedDesc')}
              icon={Activity}
              action={() => navigate('/board')}
              actionLabel={t('goBoard')}
            />
          ) : (
            <EmptyState 
              title={t('noActiveSprintTitle')}
              description={t('noActiveSprintDesc')}
              icon={Activity}
              action={() => navigate('/board')}
              actionLabel={t('goBoard')}
            />
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col space-y-8 overflow-hidden">
          {/* Synchronized Hero Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 shrink-0">
            <StatCard 
              label={t('resolvedByYou')}
              value={mcpStats.total}
              description={t('totalTicketsHandled') || 'Total Resolved via MCP'}
              icon={CheckCircle2}
              colorClass="bg-slate-400"
              loading={loading}
            />
            <StatCard 
              label={t('appliedLocal')}
              value={mcpStats.applied}
              description={t('waitForSync') || 'Ready for Sync'}
              icon={Activity}
              colorClass="bg-indigo-400"
              cardBg="bg-indigo-500/10 border-indigo-500/20"
              loading={loading}
            />
            <StatCard 
              label={t('readyForReview')}
              value={mcpStats.ready}
              description={t('deploymentSuccess') || 'Verified Live Today'}
              icon={Rocket}
              colorClass="bg-emerald-400"
              cardBg="bg-emerald-500/10 border-emerald-500/20"
              loading={loading}
            />
          </div>

          {/* Regression Feed */}
          <div className="flex-1 min-h-0 flex flex-col space-y-6 pb-0 relative">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-3">
                    <History size={18} className="text-slate-600" />
                    <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">
                        {t('regressionEventLog')}
                        <span className="ml-2 text-primary tabular-nums">({mcpStats.total})</span>
                    </h2>
                </div>
                
                {/* Repositioned Search Bar & Filter */}
                <div className="flex items-center gap-3">
                  <div className="relative group">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-primary transition-all duration-300" size={16} />
                      <input 
                          type="text"
                          placeholder={t('searchPlaceholder')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="bg-white/[0.02] border border-white/10 rounded-xl py-2.5 pl-12 pr-6 text-xs focus:outline-none focus:border-primary/50 focus:bg-white/[0.05] transition-all w-full md:w-64 text-slate-300 placeholder:text-slate-600 shadow-lg backdrop-blur-md"
                      />
                  </div>
                  <button 
                    onClick={() => setIsFilterModalOpen(true)}
                    className={`p-2.5 rounded-xl border transition-all duration-300 flex items-center gap-2 group ${
                      filters.dateRange.type !== 'all' 
                        ? 'bg-primary/10 border-primary/30 text-primary' 
                        : 'bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05] hover:border-white/20'
                    }`}
                  >
                    <Filter size={16} className={filters.dateRange.type !== 'all' ? 'fill-primary/20' : ''} />
                    {filters.dateRange.type !== 'all' && (
                      <span className="text-[10px] font-black uppercase tracking-widest hidden md:block">
                        {t('filtered')}
                      </span>
                    )}
                  </button>
                </div>
            </div>
            <div className="h-px w-full bg-gradient-to-r from-white/5 via-white/5 to-transparent px-2" />

            <AnimatePresence mode="popLayout">
              {loading && filteredTickets.length === 0 ? (
                <div className="space-y-4 pr-4">
                  <TicketSkeleton />
                </div>
              ) : filteredTickets.length > 0 ? (
                <div 
                   ref={listRef} 
                   className="flex-1 min-h-0 overflow-y-auto pr-4 custom-scrollbar overflow-x-hidden"
                   style={{ contain: 'none', paddingBottom: '100px' }}
                >
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const ticket = filteredTickets[virtualRow.index];
                      if (!ticket) return null;
                      
                      const isRecentlyProcessed = updating[ticket._id];

                      return (
                        <div
                          key={ticket._id}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          className="hover:z-50 relative transition-all"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                            paddingBottom: '8px'
                          }}
                        >
                          <TicketItem 
                            ticket={ticket}
                            isRecentlyProcessed={isRecentlyProcessed}
                            handleCopyId={handleCopyId}
                            copyFeedback={copyFeedback}
                            handleMarkAsLive={handleMarkAsLive}
                            lang={lang}
                            t={t}
                          />
                        </div>
                      );
                    })}
                  </div>
                  
                  {!isLocalSearch && infiniteData?.[infiniteData.length - 1]?.pagination?.hasMore && (
                    <div ref={loadMoreRef} className="py-12 flex flex-col items-center justify-center gap-3 opacity-50">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('loadingMore')}</span>
                      </div>
                      <div className="text-[8px] font-bold text-slate-600 uppercase tracking-widest animate-pulse">Página {size} / {infiniteData[0]?.pagination?.total ? Math.ceil(infiniteData[0].pagination.total / 5) : '?'}</div>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState 
                  title={t('noResolvedTickets') || 'No resolved tickets found'}
                  description={t('onlyYourTicketsDesc') || 'You haven\'t resolved any tickets yet with your AI agent in this project.'}
                  icon={ShieldCheck}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {createPortal(
        <FilterModal 
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          t={t}
          showPriority={false}
          showDateRange={true}
          showStatus={false}
          showPlanning={false}
          showTags={false}
          showCreator={false}
          selectedDateRange={filters.dateRange}
          onApply={(updatedFilters) => setFilters(updatedFilters)}
        />,
        document.body
      )}
    </div>
  );
};

export default RegressionTracking;
